import React, { useEffect, useRef, useState } from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, ExclamationTriangleIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import {
  useGetObservationQuery,
  useUpdateObservationMutation,
  useWithdrawObservationMutation,
  useMarkObservationReadMutation,
  useListMyObservationsQuery,
  useLocalGetLocationByIdQuery,
} from '../../store/api/localApi';
import ConfirmDialog from '../../components/ConfirmDialog';
import ObservationLearningGoals from '../../components/observations/ObservationLearningGoals';
import { useToast } from '../../components/Toast';
import { DeliveryStatusBadge, PushStatusBadge } from '../../components/delivery/DeliveryBadges';
import { ObservationStatus, ObservationVisibility, MessageDeliveryStatus } from '../../types';
import './ObservationDetailPage.print.css';

function formatDateTime(iso: string): string {
  return formatDublinDateTime(iso);
}

const ObservationDetailPage: React.FC = () => {
  const { observationId } = useParams<{ observationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();

  const { data, isLoading, error } = useGetObservationQuery(observationId ?? '', {
    skip: !observationId,
  });
  // We need the parent's delivery id (if any) to mark-as-read. Only the
  // inbox query knows that — the observation row itself doesn't carry it.
  // For non-parent viewers (author, admin), this query just returns their
  // empty inbox and we skip the mark-read step entirely.
  const { data: inboxData } = useListMyObservationsQuery(undefined);
  // Location name for the print header, fetched by the observation's OWN
  // locationId (not the viewer's active context) — same reasoning as
  // IncidentDetail: a printout must name the record's real location.
  const { data: recordLocation } = useLocalGetLocationByIdQuery(
    data?.observation.locationId ?? '',
    { skip: !data?.observation.locationId }
  );

  const [updateObservation, { isLoading: isSaving }] = useUpdateObservationMutation();
  const [withdrawObservation, { isLoading: isWithdrawing }] = useWithdrawObservationMutation();
  const [markRead] = useMarkObservationReadMutation();

  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  // "Printed on" must be the moment of printing, not of page load — a tablet
  // can sit on this page for hours. beforeprint fires for the Print button
  // and for desktop Ctrl+P alike; the load time is only the fallback.
  const [printedAt, setPrintedAt] = useState(() => new Date().toISOString());
  useEffect(() => {
    const stampPrintTime = () => setPrintedAt(new Date().toISOString());
    window.addEventListener('beforeprint', stampPrintTime);
    return () => window.removeEventListener('beforeprint', stampPrintTime);
  }, []);

  const goBack = React.useCallback(() => {
    const idx = (location.state as { idx?: number } | null)?.idx;
    if (idx === undefined || idx === 0) {
      navigate('..', { relative: 'path' });
    } else {
      navigate(-1);
    }
  }, [navigate, location.state]);

  // Mark-as-read fires once per visit. We guard via a ref so a re-render
  // (e.g. the inbox query landing) doesn't re-fire.
  const markedRef = useRef(false);
  useEffect(() => {
    if (markedRef.current) return;
    if (!observationId) return;
    if (!data?.observation) return;
    if (!inboxData) return; // wait for inbox to settle
    const myDelivery = inboxData.deliveries.find((d) => d.observationId === observationId);
    if (myDelivery && myDelivery.status === MessageDeliveryStatus.UNREAD) {
      markedRef.current = true;
      markRead(myDelivery.id);
    }
  }, [observationId, data, inboxData, markRead]);

  if (!observationId) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-gray-500">No observation ID provided.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-12 text-center" data-testid="observation-detail-page-loading">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <p className="mt-2 text-sm text-gray-500">Loading observation…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-12 text-center" data-testid="observation-detail-page-error">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load observation</h3>
        <p className="mt-1 text-sm text-gray-500">Please try again later.</p>
        <button
          type="button"
          onClick={goBack}
          className="mt-4 text-sm text-primary-600 hover:text-primary-700"
        >
          Go back
        </button>
      </div>
    );
  }

  // learningGoals defaults to [] for a backend that predates the field (the
  // portal bundle can go live before the lambdas finish redeploying).
  const { observation, photoUrls, deliveries, learningGoals = [] } = data;
  const isAuthor = !!user && observation.authorId === user.id;
  const isWithdrawn = observation.status === ObservationStatus.WITHDRAWN;
  // The backend re-enforces the same window. UI just hides the affordance
  // when there's no point clicking — a stale window still 403s if the
  // user beats the server clock.
  const canEdit = !isWithdrawn && (isAuthor || isAdmin);
  const canWithdraw = !isWithdrawn && (isAuthor || isAdmin);
  const isStaffOnly = observation.visibility === ObservationVisibility.STAFF_ONLY;

  const startEdit = () => {
    setEditText(observation.text);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditText('');
  };

  const saveEdit = async () => {
    if (!editText.trim()) {
      showError('Observation text cannot be empty.');
      return;
    }
    try {
      await updateObservation({ id: observationId, text: editText.trim() }).unwrap();
      showSuccess('Observation updated.');
      setEditing(false);
    } catch (err: unknown) {
      const apiError = err as { data?: { error?: string; message?: string } };
      showError(apiError?.data?.error || apiError?.data?.message || 'Failed to update.');
    }
  };

  const handleWithdraw = async () => {
    setShowWithdrawConfirm(false);
    try {
      await withdrawObservation(observationId).unwrap();
      showSuccess('Observation withdrawn.');
      goBack();
    } catch (err: unknown) {
      const apiError = err as { data?: { error?: string } };
      showError(apiError?.data?.error || 'Failed to withdraw observation.');
    }
  };

  return (
    <>
      <div className="py-6" data-testid="observation-detail-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <button
            type="button"
            data-testid="observation-detail-back"
            onClick={goBack}
            className="observation-print-only-hide inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>

          {/* Print-only header. Hidden on screen (Tailwind `hidden`), surfaced
              by the print stylesheet: paper loses the nav and back button, so
              the page carries its own self-contained heading. It replaces the
              card's on-screen title + metadata on paper (hidden there) so the
              same facts aren't printed twice. */}
          <div
            data-testid="observation-detail-print-header"
            className="observation-print-only hidden border-b-2 border-gray-800 pb-3"
          >
            <h1 className="text-lg font-bold text-gray-900">Observation</h1>
            <dl className="mt-1 text-sm text-gray-700 grid grid-cols-2 gap-x-4">
              {recordLocation?.name && (
                <div>
                  <dt className="inline font-medium">Location: </dt>
                  <dd className="inline">{recordLocation.name}</dd>
                </div>
              )}
              <div>
                <dt className="inline font-medium">Child: </dt>
                <dd className="inline">{observation.studentName}</dd>
              </div>
              <div>
                <dt className="inline font-medium">From: </dt>
                <dd className="inline">
                  {observation.authorName} ({observation.authorRole})
                </dd>
              </div>
              <div>
                <dt className="inline font-medium">Session: </dt>
                <dd className="inline">{observation.date}</dd>
              </div>
              <div>
                <dt className="inline font-medium">Posted: </dt>
                <dd className="inline">{formatDateTime(observation.createdAt)}</dd>
              </div>
              {observation.editedAt && (
                <div>
                  <dt className="inline font-medium">Edited: </dt>
                  <dd className="inline">{formatDateTime(observation.editedAt)}</dd>
                </div>
              )}
            </dl>
            <p className="mt-1 text-xs text-gray-500">Printed on {formatDateTime(printedAt)}</p>
          </div>

          <div
            className="bg-white shadow rounded-lg overflow-hidden"
            data-testid="observation-detail-card"
          >
            {/* On paper only the badges survive from this section (title,
                metadata and buttons are screen-only), so without a badge it
                would print as an empty padded band. */}
            <div
              className={`px-6 py-5 ${
                isStaffOnly || isWithdrawn ? '' : 'observation-print-only-hide'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h1 className="observation-print-only-hide text-lg font-bold text-gray-900 truncate">
                    About {observation.studentName}
                  </h1>
                  <dl className="observation-print-only-hide mt-2 grid grid-cols-1 gap-y-1 sm:grid-cols-2 text-sm text-gray-600">
                    <div>
                      <dt className="inline font-medium text-gray-700">From: </dt>
                      <dd className="inline" data-testid="observation-detail-author">
                        {observation.authorName}{' '}
                        <span className="text-gray-400">({observation.authorRole})</span>
                      </dd>
                    </div>
                    <div>
                      <dt className="inline font-medium text-gray-700">Session: </dt>
                      <dd className="inline" data-testid="observation-detail-session">
                        {observation.date}
                      </dd>
                    </div>
                    <div>
                      <dt className="inline font-medium text-gray-700">Posted: </dt>
                      <dd className="inline">{formatDateTime(observation.createdAt)}</dd>
                    </div>
                    {observation.editedAt && (
                      <div>
                        <dt className="inline font-medium text-gray-700">Edited: </dt>
                        <dd className="inline">{formatDateTime(observation.editedAt)}</dd>
                      </div>
                    )}
                  </dl>
                  {isStaffOnly && (
                    <span className="inline-block mt-2 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                      Staff only
                    </span>
                  )}
                  {isWithdrawn && (
                    <span className="inline-block mt-2 ml-2 text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                      Withdrawn
                    </span>
                  )}
                </div>

                <div className="observation-print-only-hide flex-shrink-0 flex gap-2">
                  {!editing && (
                    <button
                      type="button"
                      data-testid="observation-detail-print-btn"
                      onClick={() => window.print()}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <PrinterIcon className="h-4 w-4" aria-hidden="true" />
                      Print
                    </button>
                  )}
                  {canEdit && !editing && (
                    <button
                      type="button"
                      data-testid="observation-detail-edit-btn"
                      onClick={startEdit}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Edit
                    </button>
                  )}
                  {canWithdraw && (
                    <button
                      type="button"
                      data-testid="observation-detail-withdraw-btn"
                      onClick={() => setShowWithdrawConfirm(true)}
                      disabled={isWithdrawing}
                      className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {isWithdrawing ? 'Withdrawing…' : 'Withdraw'}
                    </button>
                  )}
                  {isWithdrawn && (
                    <button
                      type="button"
                      disabled
                      data-testid="observation-detail-withdraw-disabled"
                      className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
                    >
                      Withdrawn
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 px-6 py-5">
              {editing ? (
                <div className="observation-print-only-hide space-y-3">
                  <textarea
                    data-testid="observation-detail-edit-textarea"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value.slice(0, 2000))}
                    className="block w-full rounded-md border-gray-300 h-32 resize-y"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      data-testid="observation-detail-edit-cancel"
                      onClick={cancelEdit}
                      className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-testid="observation-detail-edit-save"
                      onClick={saveEdit}
                      disabled={isSaving}
                      className="px-3 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                    >
                      {isSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <p
                  className="text-sm text-gray-800 whitespace-pre-wrap"
                  data-testid="observation-detail-text"
                >
                  {observation.text}
                </p>
              )}
            </div>

            {photoUrls.length > 0 && (
              <div
                className="border-t border-gray-100 px-6 py-5"
                data-testid="observation-detail-photos"
              >
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                  Photos
                </h2>
                <div className="observation-print-photo-grid grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {photoUrls.map((url, idx) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`observation-detail-photo-${idx}`}
                      className="observation-print-photo block rounded overflow-hidden border border-gray-200 hover:opacity-90"
                    >
                      <img
                        src={url}
                        alt={`Observation photo ${idx + 1}`}
                        className="observation-print-photo-image object-cover w-full h-32"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {learningGoals.length > 0 && (
              <div
                className="border-t border-gray-100 px-6 py-5"
                data-testid="observation-detail-learning-goals"
              >
                {/* Same gate as the audit below: the backend only returns
                    `deliveries` to author/admin viewers, so its absence means
                    a parent recipient. */}
                <ObservationLearningGoals
                  goals={learningGoals}
                  variant={deliveries ? 'staff' : 'parent'}
                  authorName={observation.authorName}
                />
              </div>
            )}

            {isWithdrawn && observation.withdrawnAt && (
              <div className="border-t border-gray-100 px-6 py-3 bg-gray-50">
                <p
                  className="text-xs text-gray-500"
                  data-testid="observation-detail-withdrawn-info"
                >
                  Withdrawn on {formatDateTime(observation.withdrawnAt)}.
                </p>
              </div>
            )}
          </div>

          {/* Per-recipient delivery audit — only returned by the backend
              for author / admin / super-admin viewers, so the absence of
              `deliveries` IS the gate (parent recipients never see it). */}
          {deliveries && (
            <div
              className="observation-print-only-hide bg-white shadow rounded-lg overflow-hidden"
              data-testid="observation-detail-audit"
            >
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900">
                  Delivery audit ({deliveries.length})
                </h2>
              </div>

              {deliveries.length === 0 ? (
                <p
                  className="px-6 py-8 text-sm text-gray-500 text-center"
                  data-testid="observation-detail-audit-empty"
                >
                  No delivery records found.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table
                    className="min-w-full divide-y divide-gray-200 text-sm"
                    data-testid="observation-detail-audit-table"
                  >
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Recipient ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Read at
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Push
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {deliveries.map((d) => (
                        <tr
                          key={d.id}
                          data-testid={`observation-detail-audit-row-${d.id}`}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-4 py-3 font-mono text-xs text-gray-700 max-w-xs truncate">
                            {d.recipientUserId}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{d.recipientRole}</td>
                          <td className="px-4 py-3">
                            <DeliveryStatusBadge status={d.status} />
                          </td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                            {d.readAt ? formatDateTime(d.readAt) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <PushStatusBadge status={d.pushStatus} />
                            {d.pushError && (
                              <p
                                className="mt-0.5 text-xs text-red-500 truncate max-w-xs"
                                title={d.pushError}
                              >
                                {d.pushError}
                              </p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={showWithdrawConfirm}
        onCancel={() => setShowWithdrawConfirm(false)}
        onConfirm={handleWithdraw}
        title="Withdraw observation?"
        message="This removes the observation from every parent inbox. Unlike messages, withdrawal works even if a parent has already read it."
        confirmLabel="Withdraw"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isWithdrawing}
      />
    </>
  );
};

export default ObservationDetailPage;
